// Who may move an issue's status where — the issue review stage's transition
// table, one module for the web and the server, so the two can't drift (issue
// review stage spec, rulings 1 and 2). Not under `client/`: the backend reads
// it too.
//
// The table is TRUSSTOR'S (`IssueStatusChangeButton/utils.ts`), so an issue
// moves the same way whichever app touches it. A manager (`issues.manage`)
// completes, reviews and reopens; a contributor (`issues.contribute` only)
// submits a fix for review, takes the submission back, or resubmits it once
// rejected.

import { IssueStatus } from '../trusstor/issue.js';

/** A status one click may set. `rejected` is not one: it is only reached
 *  through the review, with a reason. */
export type IssueMoveTarget = Exclude<IssueStatus, IssueStatus.REJECTED>;

/** What a user's single status control on an issue does: set a status, open
 *  the review (a manager on an issue waiting for review), or nothing (no
 *  control is rendered). */
export type IssueAction = IssueMoveTarget | 'review' | null;

/** Rows are the issue's status; `manage` is the `issues.manage` column,
 *  `contribute` the contributor-only one. */
const ISSUE_ACTIONS: Record<IssueStatus, { manage: IssueAction; contribute: IssueAction }> = {
  [IssueStatus.PLANNED]: { manage: IssueStatus.COMPLETED, contribute: IssueStatus.FOR_REVIEW },
  [IssueStatus.FOR_REVIEW]: { manage: 'review', contribute: IssueStatus.PLANNED },
  [IssueStatus.REJECTED]: { manage: IssueStatus.COMPLETED, contribute: IssueStatus.FOR_REVIEW },
  [IssueStatus.COMPLETED]: { manage: IssueStatus.PLANNED, contribute: null },
};

/**
 * The status control a user gets on an issue (ruling 1). A manager doesn't
 * submit for review: on planned/rejected they complete, on forReview they
 * review (approve or reject), on completed they undo. A contributor submits
 * for review (or resubmits a rejected issue) and can undo a submission; a
 * completed issue is not theirs to reopen.
 */
export function nextIssueAction(status: IssueStatus, canManage: boolean): IssueAction {
  const row = ISSUE_ACTIONS[status];
  return canManage ? row.manage : row.contribute;
}

/**
 * Whether a contributor may move an issue Trusstor holds as `from` to `to` —
 * the server's side of {@link nextIssueAction} (ruling 2). `to` is the status
 * the caller asked for, as sent; anything the contributor column doesn't offer
 * is refused.
 */
export function contributorMayMoveIssue(from: IssueStatus, to: string): boolean {
  return nextIssueAction(from, false) === to;
}
